import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Wand2, Upload, Download, Loader2, Image as ImageIcon, X, Coins } from "lucide-react";

const FLYER_CREDITS_COST = 1;

const STYLES = [
  { key: "premium", label: "Premium", desc: "Luxe cinématographique" },
  { key: "luxury", label: "Or", desc: "Or & noir, opulent" },
  { key: "minimalist", label: "Minimaliste", desc: "Épuré, élégant" },
  { key: "bold", label: "Bold", desc: "Impactant, vibrant" },
];

const EXAMPLE_PROMPTS = [
  "Restaurant gastronomique parisien, ambiance feutrée, menu dégustation à 89€",
  "Salon de coiffure haut de gamme, offre découverte -30% sur les soins",
  "Concert jazz live, samedi 22h, cocktails offerts à l'entrée",
  "Boutique de bijoux artisanaux, collection automne nouvelle arrivée",
];

export default function FlyerStudio() {
  const { user, checkAuth } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("premium");
  const [logo, setLogo] = useState(null); // base64
  const [logoName, setLogoName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const credits = user?.credits ?? 0;
  const canAfford = credits >= FLYER_CREDITS_COST;

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo trop volumineux (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogo(reader.result); setLogoName(f.name); };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Décrivez votre publicité"); return; }
    if (!canAfford) {
      toast.error(`Crédits insuffisants (${credits}/${FLYER_CREDITS_COST} requis)`);
      return;
    }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/flyer/generate", {
        prompt, style, logo_base64: logo || undefined,
      });
      setResult(data);
      const cost = data.credits_charged || 0;
      toast.success(cost > 0 ? `Flyer généré ! ${cost} crédit débité` : "Flyer généré !");
      checkAuth(); // refresh credits
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(e.response.data?.detail || "Crédits insuffisants");
      } else {
        toast.error(e.response?.data?.detail || "Erreur génération");
      }
    } finally { setLoading(false); }
  };

  const download = () => {
    if (!result?.data_url) return;
    const a = document.createElement("a");
    a.href = result.data_url;
    a.download = `xookpub-flyer-${Date.now()}.png`;
    a.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Studio · Flyer IA</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">Créez votre <span className="gold-text">flyer publicitaire</span></h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10">
            <Coins className="w-4 h-4 text-gold" />
            <span data-testid="flyer-user-credits" className={canAfford ? "text-gold font-semibold" : "text-red-400 font-semibold"}>
              {credits} crédits
            </span>
            <span className="text-white/40">·</span>
            <span className="text-white/60">{FLYER_CREDITS_COST} / flyer</span>
          </div>
          {!canAfford && (
            <Link to="/pricing" data-testid="flyer-buy-credits" className="ghost-btn !py-2 !px-3 text-xs">Recharger</Link>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-5 space-y-5">
          <div className="studio-card p-5">
            <label className="label-mono">Description publicitaire</label>
            <textarea
              data-testid="flyer-prompt"
              value={prompt}
              onChange={(e)=>setPrompt(e.target.value)}
              placeholder="Décrivez votre publicité : produit, ambiance, message clé, public cible..."
              className="mt-2 w-full h-36 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button key={i} data-testid={`example-${i}`} onClick={()=>setPrompt(ex)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-gold/10 hover:text-gold border border-white/10 text-white/60 transition">
                  {ex.slice(0, 38)}...
                </button>
              ))}
            </div>
          </div>

          <div className="studio-card p-5">
            <label className="label-mono">Style visuel</label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {STYLES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`style-${s.key}`}
                  onClick={()=>setStyle(s.key)}
                  className={`p-3 rounded-lg border text-left transition ${style===s.key ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}
                >
                  <div className="font-heading">{s.label}</div>
                  <div className="text-xs text-white/50 mt-0.5">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="studio-card p-5">
            <label className="label-mono">Logo (optionnel)</label>
            {logo ? (
              <div className="mt-3 flex items-center gap-3 p-3 bg-obsidian-light rounded-lg border border-gold/30">
                <img src={logo} alt="logo" className="w-14 h-14 rounded-lg object-contain bg-white/5 p-1" />
                <div className="flex-1 text-sm">
                  <div className="truncate">{logoName}</div>
                  <div className="text-xs text-white/40">Sera intégré dans le flyer</div>
                </div>
                <button data-testid="remove-logo" onClick={()=>{setLogo(null); setLogoName("");}} className="text-white/60 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button
                data-testid="upload-logo"
                onClick={()=>fileRef.current?.click()}
                className="mt-3 w-full border-2 border-dashed border-white/10 hover:border-gold/40 rounded-lg p-6 text-center transition group"
              >
                <Upload className="w-6 h-6 text-white/40 group-hover:text-gold mx-auto" />
                <div className="mt-2 text-sm text-white/60">Glissez votre logo ici (PNG, JPG, max 5MB)</div>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="logo-input" />
          </div>

          <button data-testid="flyer-generate-btn" onClick={generate} disabled={loading || !canAfford} className="gold-btn w-full !py-4 text-base disabled:opacity-50">
            {loading
              ? <><Loader2 className="w-5 h-5 animate-spin" /> Génération en cours...</>
              : <><Wand2 className="w-5 h-5" /> Générer mon flyer · {FLYER_CREDITS_COST} crédit</>}
          </button>
        </div>

        {/* Right: Preview */}
        <div className="lg:col-span-7">
          <div className="studio-card p-5 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <span className="label-mono">Aperçu</span>
              {result?.data_url && (
                <button data-testid="flyer-download" onClick={download} className="ghost-btn !py-2 !px-4 text-sm">
                  <Download className="w-4 h-4" /> Télécharger
                </button>
              )}
            </div>
            <div className="aspect-square bg-obsidian rounded-xl border border-white/5 overflow-hidden flex items-center justify-center">
              {loading ? (
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full border-2 border-gold border-t-transparent animate-spin" />
                  <div className="mt-4 label-mono">Gemini compose votre visuel...</div>
                  <div className="mt-1 text-xs text-white/40">~ 20 à 40 secondes</div>
                </div>
              ) : result?.data_url ? (
                <img src={result.data_url} alt="Flyer généré" className="w-full h-full object-contain" data-testid="flyer-result-image" />
              ) : (
                <div className="text-center text-white/30">
                  <ImageIcon className="w-12 h-12 mx-auto" />
                  <div className="mt-3 text-sm">Votre flyer apparaîtra ici</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
